import { z } from "zod";

export type PasswordRequirement = {
  /** Shown in the strength meter checklist. */
  label: string;
  /** Shown as the inline field error when this is the FIRST unmet requirement. */
  message: string;
  test: (value: string) => boolean;
};

/**
 * THE Birdwatch password policy — the single home for "what is an acceptable
 * password". Both the Zod schema and the strength meter derive from this list,
 * so a policy change is one edit here.
 * `max(72)` is the bcrypt input limit, not a UX choice.
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    label: "At least 8 characters",
    message: "Use at least 8 characters.",
    test: (v) => v.length >= 8,
  },
  {
    label: "At most 72 characters",
    message: "Use at most 72 characters.",
    test: (v) => v.length <= 72,
  },
  {
    label: "1 lowercase letter",
    message: "Add at least 1 lowercase letter.",
    test: (v) => /[a-z]/.test(v),
  },
  {
    label: "1 uppercase letter",
    message: "Add at least 1 uppercase letter.",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    label: "1 number",
    message: "Add at least 1 number.",
    test: (v) => /\d/.test(v),
  },
  {
    label: "1 symbol",
    message: "Add at least 1 symbol.",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

/** True when every requirement is met. */
export const isStrongPassword = (value: string): boolean =>
  PASSWORD_REQUIREMENTS.every((requirement) => requirement.test(value));

/**
 * Reports only the FIRST unmet requirement so the inline error stays one short
 * sentence — `getFieldErrorMessage` comma-joins every issue, and the meter
 * already shows the full checklist.
 */
export const passwordSchema = z.string().superRefine((value, ctx) => {
  const failed = PASSWORD_REQUIREMENTS.find((r) => !r.test(value));
  if (failed) ctx.addIssue({ code: "custom", message: failed.message });
});

/** The "type it again" field. The match rule stays on the owning form's schema. */
export const confirmPasswordSchema = z
  .string()
  .min(1, "Please confirm your password.");
